import React, { useEffect, useState } from "react";
import { useRef } from "react";
import toast from "react-hot-toast";
import { useDispatch, useSelector } from "react-redux";
import { RootState, RootDispatch } from "../../../store";
import {
  fetchWhatsappTemplateById,
  fetchWhatsappTemplates,
  sendBulkWhatsappMessage,
} from "../../../store/slices/whatsappDataSlice";
import { uploadWarrantyCardToS3 } from "../../../Services/uploadBulkDataToS3";
import {
  Phone,
  Users,
  Upload,
  FileText,
  Send,
  ChevronDown,
  X,
  Clipboard,
} from "lucide-react";
import getMimeType from "../../../utils/getMimeType";

const WhatsAppBulkSender: React.FC = () => {
  const inputRef = useRef<HTMLInputElement | null>(null);
  const [phoneList, setPhoneList] = useState("");
  const [, setPhoneNumber] = useState("");
  const [isSending, setIsSending] = useState(false);
  const [selectedTemplateId, setSelectedTemplateId] = useState("");
  const [documentFile, setDocumentFile] = useState<Record<
    string,
    File | null
  > | null>(null);
  const [componentInputs, setComponentInputs] = useState<any>({});

  const dispatch: RootDispatch = useDispatch();
  const whatsappTemplates = useSelector(
    (state: RootState) => state.whatsAppData.templates
  );
  const singleTemplate = useSelector(
    (state: RootState) => state.whatsAppData.singleTemplate
  );

  const isLoading = useSelector(
    (state: RootState) => state.whatsAppData.loading
  );

  async function getTemplates() {
    await dispatch(fetchWhatsappTemplates());
  }

  async function getTemplatesById(id: any) {
    await dispatch(fetchWhatsappTemplateById(id));
  }

  useEffect(() => {
    getTemplates();
  }, []);

  const handleSend = async () => {
    const numbers = phoneList
      .split(",")
      .map((p) => p.trim())
      .filter((p) => p.length > 0);

    if (!numbers.length) {
      toast.error("Please enter at least one phone number.");
      return;
    }

    if (!selectedTemplateId) {
      toast.error("Please select a WhatsApp template.");
      return;
    }

    const invalidNumbers = numbers.filter((n) => !/^\+?\d{10,15}$/.test(n));
    if (invalidNumbers.length) {
      toast.error("Some phone numbers are invalid.");
      return;
    }

    const template = singleTemplate?.template;
    const templateComponents = template?.components || [];

    for (const comp of templateComponents) {
      const { type, text, format } = comp;
      const upperType = type.toUpperCase();
      const paramMatches = text?.match(/{{\s*\d+\s*}}/g) || [];

      for (let i = 0; i < paramMatches.length; i++) {
        const key = `${upperType}_${i + 1}`;
        if (!componentInputs[key] || !componentInputs[key].trim()) {
          toast.error(`Please fill out all parameters for ${upperType}.`);
          return;
        }
      }

      if (
        upperType === "HEADER" &&
        (format === "DOCUMENT" || format === "IMAGE") &&
        (!documentFile || !documentFile[upperType])
      ) {
        toast.error(`Missing ${format.toLowerCase()} file for header.`);
        return;
      }
    }

    setIsSending(true);

    try {
      const templateName = template?.name;
      const languageCode = template?.language || "en";
      const components: any[] = [];
      const uploadKeyNumber = numbers[0]; // Use first number as unique key

      // Compose components
      for (const comp of templateComponents) {
        const { type, text = "", format } = comp;
        const upperType = type.toUpperCase();

        if (
          upperType === "HEADER" &&
          (format === "DOCUMENT" || format === "IMAGE")
        ) {
          const file = documentFile?.[upperType];
          if (!file) {
            toast.error("Please upload the required document or image.");
            throw new Error("Missing header file");
          }

          const base64String = await new Promise<string>((resolve, reject) => {
            const reader = new FileReader();
            reader.onload = () =>
              resolve((reader.result as string).split(",")[1]);
            reader.onerror = reject;
            reader.readAsDataURL(file);
          });

          const mimeType = getMimeType(file.name) || "application/pdf";
          const uploadRes = await uploadWarrantyCardToS3(
            uploadKeyNumber,
            base64String,
            file.name,
            mimeType
          );

          if (!uploadRes.success || !uploadRes.url) {
            toast.error("Failed to upload file.");
            throw new Error("Upload failed");
          }

          components.push({
            type: "HEADER",
            parameters: [
              {
                type: format === "DOCUMENT" ? "document" : "image",
                document: {
                  link: uploadRes.url,
                  filename: file.name,
                },
              },
            ],
          });

          continue;
        }

        // For TEXT components
        const holes = text.match(/{{\s*\d+\s*}}/g) || [];
        const parameters = holes.map((_: any, i: any) => ({
          type: "text",
          text: componentInputs[`${upperType}_${i + 1}`] || "",
        }));

        components.push({
          type: upperType,
          parameters,
        });
      }

      // Send to each number
      const results = await Promise.allSettled(
        numbers.map((number) =>
          dispatch(
            sendBulkWhatsappMessage({
              userPhoneNumber: number,
              templateName,
              languageCode,
              components,
            })
          )
        )
      );

      const failed = results.filter((r) => r.status === "rejected");
      if (failed.length) {
        toast.error(`${failed.length} message(s) failed.`);
      } else {
        toast.success("Messages sent successfully.");
        setPhoneList("");
        setPhoneNumber("");
        setSelectedTemplateId("");
        setDocumentFile(null);
        setComponentInputs({});
      }
    } catch (err: any) {
      console.error("Send error:", err);
      toast.error("Failed to send message.");
    } finally {
      setIsSending(false);
    }
  };

  const handlePaste = async () => {
    try {
      const text = await navigator.clipboard.readText();
      const formatted = text
        .split(/[\n\r\s,]+/) // Split on newlines, spaces, or existing commas
        .map((p) => p.trim())
        .filter((p) => p.length > 0)
        .join(", ");
      setPhoneList(formatted);
      toast.success("Phone numbers pasted and formatted successfully");
    } catch (err) {
      toast.error("Failed to paste from clipboard");
    }
  };

  const countNumbers = () => {
    if (!phoneList) return 0;
    return phoneList
      .split(",")
      .map((n) => n.trim())
      .filter((n) => n.length > 0).length;
  };

  const handleSelectFile = () => {
    inputRef.current?.click();
  };

  return (
    <div className="w-full max-w-2xl mx-auto py-2 px-2 text-sm">
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
        <div className="p-3 border-b border-gray-200">
          <div className="flex items-center mb-2">
            <Users className="h-4 w-4 text-gray-500 mr-1" />
            <h2 className="text-base font-medium text-gray-800">Recipients</h2>
          </div>

          <div className="relative mt-2">
            <div className="absolute inset-y-0 left-0 pl-2 flex items-center pointer-events-none">
              <Phone className="h-3.5 w-3.5 text-gray-400" />
            </div>

            <textarea
              rows={2}
              value={phoneList}
              onChange={(e) => setPhoneList(e.target.value)}
              placeholder="Enter phone numbers, comma‑separated (e.g. +1234567890, +0987654321)"
              className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg shadow-sm focus:ring-2 focus:ring-green-500 focus:border-green-500 transition-all text-sm"
            />

            <div className="absolute inset-y-0 right-0 pr-3 flex items-center">
              <button
                onClick={handlePaste}
                className="p-1 rounded-md text-gray-500 hover:text-gray-700 hover:bg-gray-100"
                title="Paste from clipboard"
              >
                <Clipboard className="h-4 w-4" />
              </button>
            </div>
          </div>

          <div className="flex justify-between mt-2">
            <p className="text-xs font-medium text-gray-600">
              {countNumbers()} recipient{countNumbers() !== 1 ? "s" : ""}
            </p>
          </div>
        </div>

        {/* Template Section */}
        <div className="p-6 border-b border-gray-200">
          <div className="flex items-center mb-3">
            <FileText className="h-5 w-5 text-gray-500 mr-2" />
            <h2 className="text-lg font-medium text-gray-800">
              Message Template
            </h2>
          </div>

          <div className="relative">
            <select
              value={selectedTemplateId}
              onChange={(e) => {
                const id = e.target.value;
                setSelectedTemplateId(id);
                setDocumentFile(null);
                setComponentInputs({});
                if (id) {
                  getTemplatesById(id);
                }
              }}
              className="block w-full appearance-none bg-white border border-gray-300 rounded-lg py-3 px-4 pr-8 shadow-sm focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-green-500 text-sm"
              disabled={isLoading}
            >
              <option value="">Select a WhatsApp template</option>
              {whatsappTemplates?.map((template: any) => (
                <option key={template?.id} value={template?.id}>
                  {template?.name}
                </option>
              ))}
            </select>
            <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-2 text-gray-700">
              <ChevronDown className="h-4 w-4" />
            </div>
          </div>

          {isLoading && (
            <div className="mt-3 flex items-center">
              <div className="animate-spin h-4 w-4 border-2 border-green-500 border-t-transparent rounded-full mr-2"></div>
              <span className="text-sm text-gray-600">Loading template...</span>
            </div>
          )}
        </div>

        {singleTemplate?.template?.components?.length > 0 && (
          <div className="p-6">
            <div className="space-y-5">
              {singleTemplate?.template?.components?.map(
                (component: any, index: number) => {
                  const type = component.type.toUpperCase();
                  const format = component.format || "TEXT";
                  const text = component.text || "";

                  // Document/Image Upload
                  if (
                    type === "HEADER" &&
                    (format === "DOCUMENT" || format === "IMAGE")
                  ) {
                    return (
                      <div
                        key={index}
                        className="rounded-lg border border-gray-200 overflow-hidden"
                      >
                        <div className="bg-gray-50 px-4 py-3 border-b border-gray-200">
                          <h3 className="text-sm font-medium text-gray-700">
                            {format === "DOCUMENT"
                              ? "Document Attachment"
                              : "Header Image"}
                          </h3>
                        </div>

                        <div className="p-4">
                          {documentFile && documentFile[type] ? (
                            <div className="flex items-center justify-between p-3 bg-green-50 border border-green-200 rounded-lg">
                              <div className="flex items-center overflow-hidden">
                                <FileText className="h-5 w-5 flex-shrink-0 text-green-600 mr-2" />
                                <span className="text-sm text-gray-700 truncate">
                                  {documentFile[type]?.name}
                                </span>
                              </div>
                              <button
                                onClick={() =>
                                  setDocumentFile((prev) => ({
                                    ...prev,
                                    [type]: null,
                                  }))
                                }
                                className="ml-2 flex-shrink-0 text-gray-500 hover:text-red-500 focus:outline-none"
                                title="Remove file"
                              >
                                <X className="h-4 w-4" />
                              </button>
                            </div>
                          ) : (
                            <div
                              onClick={handleSelectFile}
                              className="border-2 border-dashed border-gray-300 rounded-lg p-6 flex flex-col items-center justify-center hover:border-green-400 transition-colors"
                            >
                              <Upload className="h-8 w-8 text-gray-400 mb-2" />
                              <div className="text-center">
                                <label
                                  htmlFor={`file-upload-${index}`}
                                  className="cursor-pointer text-green-600 hover:text-green-500 font-medium"
                                >
                                  Choose a file
                                  <input
                                    ref={inputRef}
                                    id={`file-upload-${index}`}
                                    type="file"
                                    accept={
                                      format === "DOCUMENT"
                                        ? ".pdf,.doc,.docx"
                                        : ".jpg,.jpeg,.png"
                                    }
                                    onChange={(e) =>
                                      setDocumentFile((prev: any) => ({
                                        ...prev,
                                        [type]: e.target.files?.[0] || null,
                                      }))
                                    }
                                    className="sr-only"
                                  />
                                  <p className="text-xs text-gray-500 mt-1">
                                    {format === "DOCUMENT"
                                      ? "PDF, DOC, DOCX up to 5MB"
                                      : "JPG, PNG up to 4MB"}
                                  </p>
                                </label>
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  }

                  // Text Parameter Inputs
                  const paramMatches = text.match(/{{\s*\d+\s*}}/g) || [];
                  if (paramMatches.length > 0) {
                    return (
                      <div
                        key={index}
                        className="rounded-lg border border-gray-200 overflow-hidden"
                      >
                        <div className="bg-gray-50 px-4 py-3 border-b border-gray-200">
                          <h3 className="text-sm font-medium text-gray-700">
                            {type === "HEADER"
                              ? "Header"
                              : type === "BODY"
                              ? "Message Body"
                              : type}{" "}
                            Parameters
                          </h3>
                        </div>

                        <div className="p-4">
                          <div className="space-y-3">
                            {paramMatches.map((param: any, i: any) => {
                              const paramNum = param.replace(/[^0-9]/g, "");
                              const key = `${type}_${i + 1}`;

                              return (
                                <div key={key}>
                                  <label
                                    className="block text-xs font-medium text-gray-700 mb-1"
                                    htmlFor={key}
                                  >
                                    Parameter {paramNum}
                                  </label>
                                  <input
                                    id={key}
                                    type="text"
                                    placeholder={`Enter value for {{${paramNum}}}`}
                                    onChange={(e) =>
                                      setComponentInputs((prev: any) => ({
                                        ...prev,
                                        [key]: e.target.value,
                                      }))
                                    }
                                    value={componentInputs[key] || ""}
                                    className="block w-full border border-gray-300 rounded-lg shadow-sm p-2.5 text-sm focus:ring-2 focus:ring-green-500 focus:border-green-500"
                                  />
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      </div>
                    );
                  }

                  return null;
                }
              )}
            </div>
          </div>
        )}

        {/* Send Button Section */}
        <div className="px-6 py-4 bg-gray-50 border-t border-gray-200">
          <button
            onClick={handleSend}
            disabled={isSending || !selectedTemplateId || !phoneList.trim()}
            className="w-full flex items-center justify-center space-x-2 bg-green-600 hover:bg-green-700 text-white py-3 px-4 rounded-lg shadow-sm disabled:opacity-50 disabled:cursor-not-allowed transition-all focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2"
          >
            {isSending ? (
              <>
                <div className="animate-spin h-5 w-5 border-2 border-white border-t-transparent rounded-full"></div>
                <span>Sending messages...</span>
              </>
            ) : (
              <>
                <Send className="h-5 w-5" />
                <span>
                  Send to {countNumbers()} recipient
                  {countNumbers() !== 1 ? "s" : ""}
                </span>
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default WhatsAppBulkSender;
